import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Grid, MenuItem } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";

import InputTextField from "../../../component/input/inputTextField";
import FilterSelect from "../../../component/select/filterSelect";
import ButtonCustom from "../../../component/button/buttonCustom";
import { colors } from "../../../constants/colors";
import { IStall } from "../../../api/zone/zone.api";
import { STATUS } from "../../store/stall-manage/enum/enum";
import ZoneTable from "./ZoneTable";
import StallTable from "./StallTable";
import {
  STALL_STATUS_ALL,
  filterStalls,
  filterZoneRows,
  toZoneRows,
} from "./stallPicker.utils";

export interface IStallPickerProps {
  /** IZone[] ดิบจาก ZoneApi.list */
  mapData: any[];
  /** stallId ที่อยู่ใน dataTable (basket) ของหน้าเพจแล้ว */
  basketIds: number[];
  /** stallTypeId -> ชื่อประเภทแผงค้า เพราะ zone/list ส่งมาแค่ stallTypeId */
  stallTypeNameById: Record<number, string>;
  onAddStalls: (stalls: IStall[], zoneName: string) => void;
}

export default function StallPicker(props: IStallPickerProps) {
  const { t } = useTranslation();
  const { onAddStalls } = props;

  /** เก็บ zoneId ไม่ใช่ object — refetch ทำให้ reference เปลี่ยน snapshot จะค้าง
   *  และห้ามใช้ index เพราะ index 0 เป็น falsy (บั๊กเดิมของ map.tsx) */
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);

  const [zoneKeyword, setZoneKeyword] = useState<string>("");
  const [zonePage, setZonePage] = useState<number>(1);
  const [zonePageLimit, setZonePageLimit] = useState<number>(10);

  const [stallKeyword, setStallKeyword] = useState<string>("");
  const [stallStatus, setStallStatus] = useState<string>(STALL_STATUS_ALL);
  const [stallPage, setStallPage] = useState<number>(1);
  const [stallPageLimit, setStallPageLimit] = useState<number>(10);

  /** buffer ที่ผู้ใช้ติ๊กไว้ ยังไม่เข้า basket — ผูกกับโซนที่เปิดอยู่เท่านั้น */
  const [checkedIds, setCheckedIds] = useState<number[]>([]);

  const zoneRows = useMemo(() => toZoneRows(props.mapData), [props.mapData]);
  const activeZone = useMemo(
    () =>
      selectedZoneId == null
        ? null
        : zoneRows.find((z) => z.zoneId === selectedZoneId) ?? null,
    [zoneRows, selectedZoneId]
  );

  const filteredZoneRows = useMemo(
    () => filterZoneRows(zoneRows, zoneKeyword),
    [zoneRows, zoneKeyword]
  );
  const filteredStalls = useMemo(
    () => (activeZone ? filterStalls(activeZone.stall, stallKeyword, stallStatus) : []),
    [activeZone, stallKeyword, stallStatus]
  );

  /** โซนที่เปิดอยู่หายไปหลัง refetch → เด้งกลับ level 1 และล้าง buffer
   *  ไม่งั้น buffer ของโซนเก่าจะหลุดเข้า basket พร้อมชื่อโซนผิด */
  useEffect(() => {
    if (selectedZoneId != null && activeZone == null) {
      setSelectedZoneId(null);
      setCheckedIds([]);
    }
  }, [selectedZoneId, activeZone]);

  useEffect(() => {
    setZonePage(1);
  }, [zoneKeyword]);

  useEffect(() => {
    setStallPage(1);
  }, [selectedZoneId, stallKeyword, stallStatus]);

  const handleOpenZone = useCallback((zoneId: number) => {
    setSelectedZoneId(zoneId);
    setCheckedIds([]);
    setStallKeyword("");
    setStallStatus(STALL_STATUS_ALL);
  }, []);

  const handleBack = useCallback(() => {
    setSelectedZoneId(null);
    setCheckedIds([]);
  }, []);

  const handleToggle = useCallback((stallId: number) => {
    if (stallId == null) return;
    setCheckedIds((prev) =>
      prev.includes(stallId) ? prev.filter((id) => id !== stallId) : [...prev, stallId]
    );
  }, []);

  const handleToggleAllOnPage = useCallback(
    (stallIdsOnPage: number[], nextChecked: boolean) => {
      setCheckedIds((prev) => {
        if (!nextChecked) return prev.filter((id) => !stallIdsOnPage.includes(id));
        const merged = new Set(prev);
        stallIdsOnPage.forEach((id) => merged.add(id));
        return Array.from(merged);
      });
    },
    []
  );

  const handleAdd = useCallback(() => {
    if (!activeZone) return;
    /** ดึงจากแผงทั้งโซน ไม่ใช่จากหน้าที่กรอง/แบ่งหน้าแล้ว
     *  เพราะผู้ใช้ติ๊กคร่อมหน้าได้ */
    const picked = activeZone.stall.filter((s) =>
      checkedIds.includes(s.stallId as number)
    );
    if (!picked.length) return;
    onAddStalls(picked, activeZone.zoneName);
    setCheckedIds([]);
  }, [activeZone, checkedIds, onAddStalls]);

  return (
    <Box
      sx={{
        border: `1px solid ${colors.lightGray}`,
        borderRadius: "10px",
        padding: "16px",
        width: "100%",
      }}
    >
      {activeZone ? (
        <>
          <Grid container spacing={2} alignItems="center" className="pb-3">
            <Grid item xs={12} md="auto">
              <ButtonCustom
                variant="outlined"
                startIcon={<FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: 12 }} />}
                textButton={t("MARKET_MAP.BUTTON.BACK")}
                onClick={handleBack}
              />
            </Grid>
            <Grid item xs={12} md="auto">
              <span style={{ fontSize: "1.125rem", fontWeight: 500 }}>
                {t("MARKET_MAP.HEADCELL.ZONE_H")} : {activeZone.zoneName}
              </span>
            </Grid>
          </Grid>

          <Grid container spacing={2} className="pb-3">
            <Grid item lg={3} md={6} xs={12}>
              <InputTextField
                label={t("MARKET_MAP.FILTER.SEARCH_STALL")}
                value={stallKeyword}
                onchange={(e: any) => setStallKeyword(e.target.value)}
              />
            </Grid>
            <Grid item lg={3} md={6} xs={12}>
              <FilterSelect
                labelId="label-stall-status"
                selectId="select-stall-status"
                label={t("MARKET_MAP.HEADCELL.STATUS_STALL")}
                value={stallStatus}
                onchange={(e: any) => setStallStatus(e.target.value)}
                renderValue={() =>
                  `${t("MARKET_MAP.HEADCELL.STATUS_STALL")}: ${
                    stallStatus === STALL_STATUS_ALL
                      ? t("MARKET_MAP.FILTER.OPTION.ALL")
                      : t(`MARKET_MAP.FILTER.OPTION.${stallStatus}`)
                  }`
                }
                options={[
                  <MenuItem key="ALL" value={STALL_STATUS_ALL}>
                    {t("MARKET_MAP.FILTER.OPTION.ALL")}
                  </MenuItem>,
                  <MenuItem key={STATUS.BLANK} value={STATUS.BLANK}>
                    {t("MARKET_MAP.FILTER.OPTION.BLANK")}
                  </MenuItem>,
                  <MenuItem key={STATUS.OPEN} value={STATUS.OPEN}>
                    {t("MARKET_MAP.FILTER.OPTION.OPEN")}
                  </MenuItem>,
                  <MenuItem key={STATUS.RESERVE} value={STATUS.RESERVE}>
                    {t("MARKET_MAP.FILTER.OPTION.RESERVE")}
                  </MenuItem>,
                  <MenuItem key={STATUS.CONTRACT} value={STATUS.CONTRACT}>
                    {t("MARKET_MAP.FILTER.OPTION.CONTRACT")}
                  </MenuItem>,
                  <MenuItem key={STATUS.NON_INFO} value={STATUS.NON_INFO}>
                    {t("MARKET_MAP.FILTER.OPTION.NON_INFO")}
                  </MenuItem>,
                ]}
                formControlStyle={{ maxWidth: "100% !important" }}
              />
            </Grid>
          </Grid>

          <StallTable
            rows={filteredStalls}
            page={stallPage}
            pageLimit={stallPageLimit}
            checkedIds={checkedIds}
            basketIds={props.basketIds}
            stallTypeNameById={props.stallTypeNameById}
            onToggle={handleToggle}
            onToggleAllOnPage={handleToggleAllOnPage}
            onPageChange={setStallPage}
            onPageLimitChange={setStallPageLimit}
          />

          <div className="d-flex justify-content-end pt-3">
            <ButtonCustom
              mode="add"
              disabled={checkedIds.length === 0}
              textButton={`${t("MARKET_MAP.BUTTON.ADD_SELECTED")} (${checkedIds.length})`}
              onClick={handleAdd}
            />
          </div>
        </>
      ) : (
        <>
          <Grid container spacing={2} className="pb-3">
            <Grid item lg={3} md={6} xs={12}>
              <InputTextField
                label={t("MARKET_MAP.FILTER.SEARCH_ZONE")}
                value={zoneKeyword}
                onchange={(e: any) => setZoneKeyword(e.target.value)}
              />
            </Grid>
          </Grid>

          <ZoneTable
            rows={filteredZoneRows}
            page={zonePage}
            pageLimit={zonePageLimit}
            onPageChange={setZonePage}
            onPageLimitChange={setZonePageLimit}
            onOpenZone={handleOpenZone}
          />
        </>
      )}
    </Box>
  );
}
